/**
 * React hook for fetching credits scoped to the active organization.
 *
 * Reads credits from the metering service with the X-Organization-ID header,
 * ensuring credits come from the org pool rather than per-user.
 *
 * @module lib/useOrgCredits
 */

import { useState, useEffect, useCallback } from 'react';
import { useSupabase } from './SupabaseProvider';
import { useOrg } from './OrgProvider';
import { fetchCredits, type CreditBalance } from './metering';
import { captureException } from '@/lib/telemetry/posthog/frontend';

/** Return type for the useOrgCredits hook. */
interface UseOrgCreditsReturn {
  /** Current org credit balance, or null if not yet loaded / error. */
  credits: CreditBalance | null;
  /** Whether the initial fetch is in progress. */
  loading: boolean;
  /** Whether the fetch failed. */
  error: boolean;
  /** Re-fetch credits on demand. */
  refresh: () => void;
}

/**
 * Fetches and returns the active organization's credit balance.
 * Includes X-Organization-ID header in the metering API request. Refetches
 * when the active org changes, when `refresh()` is called, or when
 * `OrgProvider.billingRefreshKey` bumps (trial activation / extension /
 * onboarding acknowledgement).
 */
export function useOrgCredits(): UseOrgCreditsReturn {
  const { supabase } = useSupabase();
  const { activeOrg, isLoading: orgLoading, billingRefreshKey } = useOrg();

  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (orgLoading || !activeOrg?.id) {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          if (!cancelled) {
            setCredits(null);
            setError(true);
            setLoading(false);
          }
          return;
        }

        const result = await fetchCredits(session.access_token, activeOrg.id);
        if (!cancelled) {
          setCredits(result);
          setError(result === null);
          setLoading(false);
        }
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'useOrgCredits.load',
          { orgId: activeOrg.id },
        );
        if (!cancelled) {
          setCredits(null);
          setError(true);
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [supabase, activeOrg?.id, orgLoading, refreshKey, billingRefreshKey]);

  return { credits, loading, error, refresh };
}
